import React, { useEffect, useState } from 'react';
import { BOAPI } from "../service/UsersService";

// הטקסט שמופיע לפני שם הפרשה באפליקציה. ברירת מחדל "פרשת"; "אחר" = טקסט חופשי.
const LABEL_PRESETS = ['פרשת', 'חג', 'שבת חול המועד'];
const OTHER = '__other__';

// לאיזה מיקום השורה חלה: יש שבועות שבהם הפרשה/החג שונים בארץ ובחו"ל. שורה ספציפית קודמת לשורה "לכולם".
const LOCATION_OPTIONS = [
  { value: '', label: 'כולם (ארץ ישראל וחו"ל)' },
  { value: 'IL', label: 'בארץ ישראל בלבד' },
  { value: 'DIASPORA', label: 'בחוץ לארץ בלבד' },
];

const defaultNewParasha = () => ({
  name: '',
  label: 'פרשת',
  location: '',
  path: '',
  startDate: '',
  endDate: '',
});

const validateParasha = (data) => {
  if (!data.name?.trim()) return 'נא להזין שם פרשה';
  if (!data.path?.trim()) return 'נא להזין נתיב';
  if (!data.startDate || !data.endDate) return 'נא להזין תאריכי התחלה וסיום';
  if (new Date(data.endDate) <= new Date(data.startDate)) return 'תאריך הסיום חייב להיות אחרי תאריך ההתחלה';
  return null;
};

// שדות הטופס המשותפים בין הוספה לעריכה.
const ParashaFields = ({ data, onChange }) => {
  const isPreset = LABEL_PRESETS.includes(data.label);
  const [otherMode, setOtherMode] = React.useState(!isPreset);
  const selectValue = otherMode || !isPreset ? OTHER : data.label;
  return (
  <>
    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>סוג (מופיע לפני השם)</label>
        <select
          value={selectValue}
          onChange={(e) => {
            if (e.target.value === OTHER) { setOtherMode(true); onChange('label', ''); }
            else { setOtherMode(false); onChange('label', e.target.value); }
          }}
          style={s.input}
        >
          {LABEL_PRESETS.map((l) => <option key={l} value={l}>{l}</option>)}
          <option value={OTHER}>אחר (טקסט חופשי / ללא)</option>
        </select>
      </div>
      {selectValue === OTHER && (
        <div style={s.formGroup}>
          <label style={s.label}>טקסט חופשי (ריק = בלי תווית)</label>
          <input
            type="text"
            value={data.label}
            onChange={(e) => onChange('label', e.target.value)}
            style={s.input}
            maxLength={24}
            placeholder="לדוגמה: מוצאי שבת"
          />
        </div>
      )}
    </div>

    <div style={s.formGroup}>
      <label style={s.label}>מיקום</label>
      <select value={data.location || ''} onChange={(e) => onChange('location', e.target.value)} style={s.input}>
        {LOCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>

    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>שם הפרשה *</label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange('name', e.target.value)}
          style={s.input}
          maxLength={16}
          placeholder="לדוגמה: בראשית"
        />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>נתיב *</label>
        <input
          type="text"
          value={data.path}
          onChange={(e) => onChange('path', e.target.value)}
          style={s.input}
          maxLength={16}
          placeholder="לדוגמה: 05/11/"
        />
      </div>
    </div>

    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>תאריך התחלה *</label>
        <input
          type="date"
          value={data.startDate}
          onChange={(e) => onChange('startDate', e.target.value)}
          style={s.input}
        />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>תאריך סיום *</label>
        <input
          type="date"
          value={data.endDate}
          onChange={(e) => onChange('endDate', e.target.value)}
          style={s.input}
        />
      </div>
    </div>
  </>
  );
};

const ParashotTab = () => {
  const [parashot, setParashot] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newParasha, setNewParasha] = useState(defaultNewParasha());

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  useEffect(() => { loadParashot(); }, []);

  const loadParashot = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchParashot();
      if (Array.isArray(result)) {
        const [statusObj, data] = result;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) {
          setParashot(data);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בטעינת הנתונים');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async () => {
    const err = validateParasha(newParasha);
    if (err) { setError(err); return; }

    try {
      const resp = await BOAPI.addParasha({
        name: newParasha.name.trim(),
        label: newParasha.label.trim(),
        location: newParasha.location,
        path: newParasha.path.trim(),
        startDate: newParasha.startDate,
        endDate: newParasha.endDate,
      });
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadParashot();
          setShowAddForm(false);
          setNewParasha(defaultNewParasha());
          setError(null);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בהוספת הפרשה');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בהוספת הפרשה');
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewParasha(defaultNewParasha());
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      name: item.name || '',
      label: item.label ?? 'פרשת',
      location: item.location || '',
      path: item.path || '',
      startDate: item.startDate ? item.startDate.slice(0, 10) : '',
      endDate: item.endDate ? item.endDate.slice(0, 10) : '',
      isActive: !!item.isActive,
    });
  };

  const handleSaveEdit = async (id) => {
    const err = validateParasha(editData);
    if (err) { setError(err); return; }

    try {
      const resp = await BOAPI.updateParasha({
        id,
        name: editData.name.trim(),
        label: editData.label.trim(),
        location: editData.location,
        path: editData.path.trim(),
        startDate: editData.startDate,
        endDate: editData.endDate,
        isActive: editData.isActive,
      });
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadParashot();
          setEditingId(null);
          setEditData(null);
          setError(null);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בעדכון הפרשה');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בעדכון הפרשה');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`למחוק את הפרשה "${item.name}"?`)) return;
    try {
      const resp = await BOAPI.deleteParasha(item.id);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadParashot();
        } else {
          setError(statusObj[0]?.msg || 'שגיאה במחיקת הפרשה');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה במחיקת הפרשה');
    }
  };

  const formatDateRange = (start, end) => {
    const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const s1 = new Date(start).toLocaleDateString('he-IL', opts);
    const s2 = new Date(end).toLocaleDateString('he-IL', opts);
    return `${s1} — ${s2}`;
  };

  if (loading) return <div style={s.center}>טוען פרשות...</div>;

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>פרשות השבוע</h2>
        {!showAddForm && (
          <button onClick={() => setShowAddForm(true)} style={s.addButton}>➕ פרשה חדשה</button>
        )}
      </div>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      {showAddForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>פרשה חדשה</h3>
          <ParashaFields data={newParasha} onChange={(field, value) => setNewParasha((prev) => ({ ...prev, [field]: value }))} />
          <div style={s.formButtons}>
            <button onClick={handleAddSubmit} style={s.saveButton}>➕ הוסף</button>
            <button onClick={handleCancelAdd} style={s.cancelButton}>✕ בטל</button>
          </div>
        </div>
      )}

      {parashot.length === 0 ? (
        <div style={s.noData}>אין פרשות להצגה</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow}>
                <th style={s.th}>שם</th>
                <th style={s.th}>מיקום</th>
                <th style={s.th}>נתיב</th>
                <th style={s.th}>תאריכים</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {parashot.map((item) => (
                <React.Fragment key={item.id}>
                  <tr style={s.row}>
                    <td style={s.td}>{item.label ? `${item.label} ` : ''}{item.name}</td>
                    <td style={s.td}>{item.location === 'IL' ? 'בארץ' : item.location === 'DIASPORA' ? 'בחו"ל' : 'כולם'}</td>
                    <td style={s.td}>{item.path}</td>
                    <td style={s.td}>{formatDateRange(item.startDate, item.endDate)}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, backgroundColor: item.isActive ? '#e8f5e9' : '#ffebee', color: item.isActive ? '#2e7d32' : '#c62828' }}>
                        {item.isActive ? '✓ פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={s.actionButtons}>
                        <button onClick={() => (editingId === item.id ? handleCancelEdit() : handleEdit(item))} style={s.editButton}>
                          {editingId === item.id ? '✕ בטל' : '✏️ ערוך'}
                        </button>
                        <button onClick={() => handleDelete(item)} style={s.cancelButton}>🗑️ מחק</button>
                      </div>
                    </td>
                  </tr>
                  {editingId === item.id && (
                    <tr>
                      <td style={s.td} colSpan={6}>
                        <div style={s.formCard}>
                          <ParashaFields data={editData} onChange={(field, value) => setEditData((prev) => ({ ...prev, [field]: value }))} />
                          <div style={s.formGroup}>
                            <label style={s.checkboxLabel}>
                              <input
                                type="checkbox"
                                checked={editData.isActive}
                                onChange={(e) => setEditData((prev) => ({ ...prev, isActive: e.target.checked }))}
                                style={s.checkbox}
                              />
                              פעיל
                            </label>
                          </div>
                          <div style={s.formButtons}>
                            <button onClick={() => handleSaveEdit(item.id)} style={s.saveButton}>💾 שמור</button>
                            <button onClick={handleCancelEdit} style={s.cancelButton}>✕ בטל</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  addButton: { padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' },
  formTitle: { marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '18px' },
  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'ltr', textAlign: 'right', boxSizing: 'border-box' },
  checkbox: { marginLeft: '5px', cursor: 'pointer' },
  checkboxLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },

  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '15px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'middle' },

  statusBadge: { display: 'inline-block', padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  actionButtons: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  editButton: { padding: '8px 16px', backgroundColor: '#1f6feb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  saveButton: { padding: '8px 14px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default ParashotTab;
